import { type ActionEvent, Controller } from "@hotwired/stimulus"

import { debounce } from "@/helpers/utils"

export default class CarouselController extends Controller {
  static targets = [
    "indicatorIcon",
    "nextButton",
    "prevButton",
    "slideList",
    "slideListItem",
    "thumbnailListItem"
  ]
  declare readonly indicatorIconTargets: HTMLElement[]
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly prevButtonTarget: HTMLButtonElement
  declare readonly slideListTarget: HTMLOListElement
  declare readonly slideListItemTargets: HTMLLIElement[]
  declare readonly thumbnailListItemTargets: HTMLLIElement[]

  static values = {
    selectedIndex: Number,
    total: Number
  }
  declare selectedIndexValue: number
  declare readonly totalValue: number

  private slideObservers: IntersectionObserver[] = []

  initialize(): void {
    this.initSlideObservers = debounce(this.initSlideObservers.bind(this), 100)
  }

  connect(): void {
    // If carousel already interacted with (e.g. arriving via back/forward button), scroll to selected
    if (this.hasInteracted) {
      this.scrollToSelected()
    }
  }

  disconnect(): void {
    this.disconnectSlideObservers()
  }

  jumpToIndex(index: number): void {
    this.setSelectedIndex(index, false)
  }

  slideListItemTargetConnected(): void {
    this.initSlideObservers()
  }

  slideNext(): void {
    this.setSelectedIndex(this.selectedIndexValue + 1)
  }

  slidePrev(): void {
    this.setSelectedIndex(this.selectedIndexValue - 1)
  }

  slideTo({ params: { index } }: ActionEvent): void {
    this.setSelectedIndex(index)
  }

  unhideAllSlides(onUnhidden?: () => void): void {
    // CWV[INP]: Force-unhiding every remaining slide costs layout + image decode for the
    // whole gallery. Use `requestAnimationFrame` to defer until after the interaction's paint.
    window.requestAnimationFrame(() => {
      // We use `aria-hidden` to indicate slide visibility during interaction, but we use `hidden`
      // to prevent images from being loaded before such slides are visible.
      if (!this.isAllSlidesLoaded) {
        this.slideListItemTargets.forEach((slide) => (slide.hidden = false))
      }

      onUnhidden?.()
    })
  }

  private clampIndex(index: number): number {
    return Math.max(0, Math.min(index, this.slideListItemTargets.length - 1))
  }

  private disconnectSlideObservers(): void {
    this.slideObservers.forEach((observer) => observer.disconnect())
  }

  private get hasInteracted(): boolean {
    return this.slideListItemTargets.some(
      (slide, index) => index > 0 && slide.getAttribute("aria-current") === "true"
    )
  }

  private initSlideObservers(): void {
    this.disconnectSlideObservers()

    const lastSlideIndex = this.totalValue
      ? this.totalValue - 1
      : this.slideListItemTargets.length - 1

    this.slideObservers = this.slideListItemTargets.map((_, index) => {
      if (index === 0) {
        return this.slideObserver(this.prevButtonTarget)
      } else if (index === lastSlideIndex) {
        return this.slideObserver(this.nextButtonTarget)
      } else {
        return this.slideObserver()
      }
    })

    this.slideObservers.forEach((observer, index) => {
      observer.observe(this.slideListItemTargets[index]!)
    })
  }

  private get isAllSlidesLoaded(): boolean {
    return this.slideListItemTargets.every((slide) => !slide.hidden)
  }

  private scrollToSelected(smoothScroll = true): void {
    const selectedSlide = this.slideListItemTargets[this.selectedIndexValue]
    const selectedThumbnail = this.thumbnailListItemTargets[this.selectedIndexValue]

    ;[selectedSlide, selectedThumbnail].forEach((item) =>
      item?.scrollIntoView({
        behavior: smoothScroll ? "smooth" : "instant",
        block: "nearest",
        inline: "start"
      })
    )
  }

  private setSelectedIndex(index: number, smoothScroll = true): void {
    this.selectedIndexValue = this.clampIndex(index)

    this.unhideAllSlides(() => {
      this.scrollToSelected(smoothScroll)
      this.toggleAttributes()
    })
  }

  private slideObserver(navButton?: HTMLButtonElement): IntersectionObserver {
    // We toggle nav buttons for first and last slides only. We use `aria-disabled` instead of
    // `disabled` because if button disabled then it immediately loses focus and the active element
    // becomes `null`. This is problematic for a carousel in a dropdown (e.g. search calendar)
    // because the dropdown closes when it doesn't contain a focused element.

    return new IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            target.setAttribute("aria-hidden", "false")
            navButton?.setAttribute("aria-disabled", "true")
          } else {
            target.setAttribute("aria-hidden", "true")
            navButton?.removeAttribute("aria-disabled")
          }
        })
      },
      { root: this.slideListTarget, threshold: 0.99 }
    )
  }

  private toggleAttributes(): void {
    this.slideListItemTargets.forEach((slide, index) => {
      const isSelected = index === this.selectedIndexValue

      slide.setAttribute("aria-current", isSelected ? "true" : "false")
      this.indicatorIconTargets[index]?.classList.toggle("--selected", isSelected)
      this.thumbnailListItemTargets[index]?.classList.toggle("--selected", isSelected)
    })
  }
}
